import React, { useState } from 'react';

import { NamedLink } from '../../components';

import css from './SellerResources.module.css';

const ICON_PATHS = {
  book:
    'M12 5.5C9.8 3.8 7 3.4 4 4.5v13c3-1.1 5.8-.7 8 1 2.2-1.7 5-2.1 8-1v-13c-3-1.1-5.8-.7-8 1Zm0 0v13',
  camera:
    'M4 8.5A2.5 2.5 0 0 1 6.5 6H8l1.4-1.8h5.2L16 6h1.5A2.5 2.5 0 0 1 20 8.5v8A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5v-8Zm8 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  bookmark: 'M6 4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21l-6-3.5L6 21V4.5Z',
  coins:
    'M7 7c0 1.7 2.2 3 5 3s5-1.3 5-3-2.2-3-5-3-5 1.3-5 3Zm0 0v4c0 1.7 2.2 3 5 3s5-1.3 5-3V7M3 11c0 1.7 2.2 3 5 3m-5-3v4c0 1.7 2.2 3 5 3 1.1 0 2.1-.2 2.9-.6',
  parcel: 'm12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm-8 4.5 8 4.5 8-4.5M12 12v9M8 5.2l8 4.6',
  truck:
    'M3 6h11v9H3V6Zm11 3h3.5L21 12.5V15h-7V9Zm-8.5 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm11 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  card:
    'M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11ZM4 8h16m-12 5h3',
  check: 'm5 12 4 4L19 6',
  store: 'M4 9h16l-1.5-5h-13L4 9Zm1 0v10h14V9m-11 3v7m5-7v7',
  question:
    'M9.5 9a2.7 2.7 0 1 1 4.8 1.7c-1.1 1.1-2.3 1.5-2.3 3.1m0 3.3v.1M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
};

const ResourceIcon = ({ name, className }) => (
  <span className={`${css.iconCircle} ${className || ''}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" focusable="false">
      <path d={ICON_PATHS[name]} />
    </svg>
  </span>
);

const NewListingLink = ({ children, className, search }) => (
  <NamedLink className={className} name="NewListingPage" to={{ search }}>
    {children}
  </NamedLink>
);

export const RESOURCE_LINKS = [
  { topic: 'selling', title: 'Seller Guide', icon: 'book' },
  { topic: 'payments', title: 'Fees', icon: 'coins' },
  { topic: 'shipping', title: 'Shipping', icon: 'parcel' },
  { topic: 'condition', title: 'Condition Guide', icon: 'bookmark' },
  { topic: 'getting-paid', title: 'Getting Paid', icon: 'card' },
  { topic: 'seller-faqs', title: 'Seller FAQs', icon: 'question' },
  { topic: 'seller-shops', title: 'Manage Your Shop', icon: 'store' },
];

export const SellerResourceHeader = ({ title, description }) => (
  <header className={css.header}>
    <nav className={css.breadcrumb}>
      <NamedLink name="SellerHubPage">PageHop 101</NamedLink>
      <span> / {title}</span>
    </nav>
    <h1>{title}</h1>
    <p className={css.description}>{description}</p>
  </header>
);

export const SellerResourceLayout = ({ topic, children, railItems = [], keyFact }) => (
  <div className={css.guideLayout}>
    <article className={css.body}>{children}</article>
    <aside className={css.rail}>
      <div className={css.railPanel}>
        {railItems.length ? (
          <div className={css.railBlock}>
            <h2>On this page</h2>
            <ol className={css.railList}>
              {railItems.map((item, index) => (
                <li key={item}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  {item}
                </li>
              ))}
            </ol>
          </div>
        ) : null}
        <RelatedGuides topic={topic} compact />
      </div>
      {keyFact ? (
        <div className={css.railFact}>
          <ResourceIcon name="book" />
          <h2>{keyFact.title}</h2>
          <p>{keyFact.copy}</p>
        </div>
      ) : null}
    </aside>
  </div>
);

export const SellerResourceSection = ({ title, className, children }) => (
  <section className={`${css.section} ${className || ''}`}>
    {title ? <h2>{title}</h2> : null}
    {children}
  </section>
);

export const SellerResourceCallout = ({ tone = 'note', title, children }) => (
  <aside className={`${css.callout} ${css[`callout${tone}`] || ''}`}>
    <ResourceIcon name={tone === 'tip' ? 'check' : 'book'} />
    <div>
      <h3>{title}</h3>
      {children}
    </div>
  </aside>
);

export const SellerResourceChecklist = ({ items }) => (
  <ul className={css.checklist}>
    {items.map(item => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

export const SellerResourceCTA = ({
  title = 'Ready to list a book?',
  copy,
  actionLabel = 'Create a Listing',
  actionSearch,
}) => (
  <aside className={css.cta}>
    <div>
      <h2>{title}</h2>
      {copy ? <p>{copy}</p> : null}
    </div>
    <NewListingLink className={css.ctaAction} search={actionSearch}>
      {actionLabel}
    </NewListingLink>
  </aside>
);

export const RelatedGuides = ({ topic, compact = false }) => {
  const links = RESOURCE_LINKS.filter(link => link.topic !== topic).slice(0, compact ? 4 : 3);
  return (
    <section className={compact ? css.relatedCompact : css.related}>
      <h2>Keep exploring</h2>
      <div>
        {links.map(link => (
          <NamedLink
            key={link.topic}
            name="HowToUseShelfyTopicPage"
            params={{ topic: link.topic }}
            className={css.relatedLink}
          >
            <ResourceIcon name={link.icon} />
            <b>{link.title}</b>
            <i aria-hidden="true">&rarr;</i>
          </NamedLink>
        ))}
      </div>
    </section>
  );
};

export const Workflow = ({ steps, inline = false }) => (
  <ol className={inline ? css.inlineWorkflow : css.workflow}>
    {steps.map(({ label, description }, index) => (
      <li key={label}>
        <span className={css.workflowStep}>{String(index + 1).padStart(2, '0')}</span>
        <div>
          <h3>{label}</h3>
          {description ? <p>{description}</p> : null}
        </div>
      </li>
    ))}
  </ol>
);

export const AtAGlance = ({ items }) => (
  <ul className={css.atAGlance}>
    {items.map(([icon, label, copy]) => (
      <li key={label}>
        <ResourceIcon name={icon} />
        <b>{label}</b>
        <span>{copy}</span>
      </li>
    ))}
  </ul>
);

export const FeesGuide = () => (
  <>
    <SellerResourceSection title="Simple fees. No surprises.">
      <p>
        Listing is free. PageHop only takes a seller fee when an item sells, and that fee is
        calculated from the item sale price only.
      </p>
      <div className={css.feeLayout}>
        <ul className={css.feePoints}>
          {[
            ['check', 'List for free', 'No upfront listing fee.'],
            ['coins', '20% fee when sold', 'Only applied after a completed sale.'],
            ['card', 'You keep 80%', 'Your earnings are based on the item sale price.'],
            [
              'parcel',
              'Shipping excluded',
              'Buyer-paid shipping and taxes are not part of the commission.',
            ],
          ].map(([icon, label, copy]) => (
            <li key={label}>
              <ResourceIcon name={icon} />
              <span>
                <b>{label}</b>
                {copy}
              </span>
            </li>
          ))}
        </ul>
        <div className={css.feeExample}>
          <h3>$40 sale</h3>
          <div>
            <span>Item sale price</span>
            <span>$40</span>
          </div>
          <div>
            <span>PageHop fee</span>
            <span>$8</span>
          </div>
          <div className={css.feeExampleTotal}>
            <span>You keep</span>
            <span>$32</span>
          </div>
          <p>Shipping and applicable taxes are excluded from this example.</p>
        </div>
      </div>
    </SellerResourceSection>
    <SellerResourceSection title="What the fee helps cover">
      <p>
        The seller fee supports the marketplace, secure payments, seller and buyer support, and the
        tools that help books move from one shelf to the next.
      </p>
    </SellerResourceSection>
  </>
);

export const ShippingGuide = () => (
  <>
    <SellerResourceCallout title="Buyer pays shipping. Seller handles fulfillment.">
      <p>
        The buyer pays the shipping charge as part of their order. The seller is responsible for
        packing and sending the parcel using the applicable shipping method.
      </p>
    </SellerResourceCallout>
    <SellerResourceSection title="After a sale">
      <Workflow
        inline
        steps={[
          { label: 'Pack', description: 'Pack books and bookish goods securely for the trip.' },
          { label: 'Track', description: 'Add tracking when the shipping service supports it.' },
          {
            label: 'Ship',
            description: 'Send the order and confirm the details on the transaction.',
          },
        ]}
      />
    </SellerResourceSection>
    <SellerResourceSection title="Packing tips">
      <SellerResourceChecklist
        items={[
          'Use packaging that protects the item for the trip.',
          'Take extra care with dust jackets, sprayed edges, or loose extras.',
          'Check the order details before shipping so you know which service applies.',
        ]}
      />
    </SellerResourceSection>
  </>
);

const CONDITION_LEVELS = [
  ['New / Sealed', 'Brand new and still sealed in its original packaging.', 'No wear at all.'],
  [
    'New / Unsealed',
    'Brand new, but the original packaging has been opened.',
    'No shelf or reading wear.',
  ],
  ['Like New', 'Looks unread with no noticeable wear.', 'No creases, marks, or shelf wear.'],
  ['Excellent', 'Very light signs of handling.', 'Minor shelf wear only.'],
  ['Very Good', 'Shows light wear from normal use.', 'Minor creasing or edge wear, no writing.'],
  [
    'Good',
    'Shows clear signs of previous reading.',
    'Cover wear, page creasing, or minor markings.',
  ],
  [
    'Reading Copy',
    'Well used but complete and readable.',
    'Noticeable wear, may include writing or markings.',
  ],
];

export const ConditionGuide = () => (
  <>
    <SellerResourceSection title="Condition levels">
      <div className={css.conditionGrid}>
        {CONDITION_LEVELS.map(([name, definition, wear]) => (
          <article key={name} className={css.conditionCard}>
            <h3>{name}</h3>
            <p>{definition}</p>
            <span>{wear}</span>
          </article>
        ))}
      </div>
    </SellerResourceSection>
    <SellerResourceCallout title="When in doubt, grade down.">
      <p>
        A conservative condition grade builds trust and helps buyers understand exactly what they
        are receiving.
      </p>
    </SellerResourceCallout>
    <SellerResourceSection title="Practical listing guidance">
      <SellerResourceChecklist
        items={[
          'Photograph flaws clearly, including corners, spine, pages, and dust jackets.',
          'Describe damage plainly instead of relying on photos alone.',
          'Call out special-edition defects such as bent overlays, rubbed foil, or sprayed-edge marks.',
          'Mention missing extras, unsigned bookplates, damaged slipcases, or absent bonus items.',
        ]}
      />
    </SellerResourceSection>
  </>
);

export const GettingPaidGuide = () => (
  <>
    <SellerResourceSection title="Delivered to payout">
      <Workflow
        inline
        steps={[{ label: 'Delivered' }, { label: '48-hour hold' }, { label: 'Funds released' }]}
      />
    </SellerResourceSection>
    <SellerResourceSection title="Set up your payout details">
      <p>
        Complete your payout details in your account so PageHop can release your earnings through
        the configured payout system.
      </p>
    </SellerResourceSection>
    <SellerResourceSection title="When funds are released">
      <p>
        Once an order is marked delivered, the sale proceeds are held for 48 hours. If no issue is
        raised during that review period, earnings are released through PageHop&apos;s payout
        system.
      </p>
    </SellerResourceSection>
    <SellerResourceSection title="Where to see your payout">
      <p>
        Use your account payout settings and sale details to keep payout information current and
        follow the status of completed orders.
      </p>
    </SellerResourceSection>
  </>
);

const FAQ_GROUPS = [
  [
    'Listings',
    [
      [
        'What should I include in a listing?',
        'Use clear photos of the actual item and include edition, condition, included extras, price, and shipping details that matter to a buyer.',
      ],
    ],
  ],
  [
    'Selling',
    [
      [
        'How do I manage active listings?',
        'Use My Shelf to review listings, edit details, and keep your selling activity organized.',
      ],
    ],
  ],
  [
    'Shipping',
    [
      [
        'Who pays shipping?',
        'The buyer pays the shipping cost as part of their order. Sellers pack the item, ship it using the applicable method, and add tracking when available.',
      ],
    ],
  ],
  [
    'Fees',
    [
      [
        'How much does it cost to list?',
        'Listing is free. PageHop keeps 20% of the item sale price when an item sells. Shipping and taxes are not included in the commission.',
      ],
    ],
  ],
  [
    'Payouts',
    [
      [
        'When do I get paid?',
        "Once an order is marked delivered, sale proceeds are held for 48 hours. If no issue is raised, earnings are released through PageHop's payout system.",
      ],
    ],
  ],
  [
    'Account & Shop',
    [['Where do I update my shop?', 'Manage your public shop details in Profile Settings.']],
  ],
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={css.faqItem}>
      <button
        type="button"
        className={css.faqQuestion}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span>{question}</span>
        <span className={css.faqToggle} aria-hidden="true">
          {open ? '\u2212' : '+'}
        </span>
      </button>
      {open ? <p className={css.faqAnswer}>{answer}</p> : null}
    </div>
  );
};

export const SellerFAQGuide = () => (
  <>
    {FAQ_GROUPS.map(([group, questions]) => (
      <SellerResourceSection key={group} title={group} className={css.faqGroup}>
        {questions.map(([question, answer]) => (
          <FAQItem key={question} question={question} answer={answer} />
        ))}
      </SellerResourceSection>
    ))}
  </>
);

export const ShopGuide = () => (
  <>
    <SellerResourceSection title="Shop profile">
      <p>
        Your PageHop Shop brings your active listings together so buyers can browse everything you
        currently have available.
      </p>
    </SellerResourceSection>
    <SellerResourceSection title="What to set up">
      <SellerResourceChecklist
        items={[
          'Add a clear shop name and profile image.',
          'Set your Ships from province or region.',
          'Choose specialties such as Special Editions, Signed Books, Romance, Fantasy, or Bookish Goods.',
          'Keep active listings current so your shop feels reliable.',
          'Use reviews and completed transactions to build buyer trust over time.',
        ]}
      />
      <NamedLink className={css.textAction} name="ProfileSettingsPage">
        Manage Your Shop &rarr;
      </NamedLink>
    </SellerResourceSection>
    <SellerResourceSection title="What buyers see">
      <p>
        Buyers may see your shop name, avatar, bio, province, specialties, bundle offer, active
        listings, and seller reviews where available.
      </p>
    </SellerResourceSection>
  </>
);
